import { useRef } from "react";
import { IconArrowRight, IconFileUpload, IconWorld } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Button, Card, Group, PageHeader, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { SolicitudDetalle } from "@/lib/types";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/carga-invoice";
import { cargaInvoiceApi } from "@/features/carga-invoice/services/carga-invoice.api";
import { InvoiceValidacionProgreso } from "./InvoiceValidacionProgreso";
import { ResultadoValidacionesInvoice, invoiceValido } from "./ResultadoValidacionesInvoice";

/**
 * Paso de carga del subtipo INVOICE (factura extranjera). El solicitante sube el PDF,
 * el backend lo extrae por OCR según la plantilla del cliente y deriva la organización
 * del RFC receptor. Se muestran los datos extraídos para revisión antes de continuar.
 */
export function CargaInvoicePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sol } = useSolicitud(id);
  const inputRef = useRef<HTMLInputElement>(null);

  const cargar = useMutation({
    mutationFn: (pdf: File) => cargaInvoiceApi.cargar(id, pdf),
    onSuccess: (detalle: SolicitudDetalle) => {
      qc.setQueryData(qk.solicitud(id), detalle);
      toastExito({ title: "Factura extraída", message: `Plantilla ${detalle.invoice?.plantilla ?? ""} · revisa los datos.` });
    },
    onError: toastErrorApi,
  });

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) cargar.mutate(file);
  }

  const inv = sol.invoice;
  // Las validaciones OCR del Invoice (réplica del legacy) SOLO aplican a Contrato
  // Anualizado y Orden de Compra · en FB60 el Invoice se queda como en el legacy
  // (sin el panel de validaciones ni el bloqueo de continuar).
  const aplicaValidaciones = sol.tipo === "ContratoAnualizado" || sol.tipo === "OrdenDeCompra";

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Factura extranjera (Invoice)"
        description="Sube el PDF · se extrae por OCR según la plantilla del cliente."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Factura (PDF)" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} paso="cfdi" solicitudId={id} fillHeight>
        <Stack gap="md">
          <Card padding="lg">
            <Stack gap="sm">
              <Group gap="xs">
                <IconWorld size={18} className="text-multiva-600" />
                <Text fw={600}>{inv ? "Reemplazar factura (PDF)" : "Sube el PDF de la factura extranjera"}</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Se detecta el cliente por el contenido del PDF (Bloomberg, NYSE, Alberto Calva, etc.) y se extraen los
                datos por OCR. La organización se deriva del RFC receptor.
              </Text>
              <div>
                <Button
                  leftSection={<IconFileUpload size={16} />}
                  loading={cargar.isPending}
                  onClick={() => inputRef.current?.click()}
                >
                  {cargar.isPending ? "Validando información de invoice..." : inv ? "Subir otro PDF" : "Seleccionar PDF"}
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf"
                  onChange={onArchivo}
                  style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </Stack>
          </Card>

          {cargar.isPending && aplicaValidaciones && <InvoiceValidacionProgreso />}

          {inv && !(cargar.isPending && aplicaValidaciones) && (
            <Card padding="lg">
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Text fw={600}>Datos extraídos</Text>
                  <Badge variant="light">{inv.plantilla}</Badge>
                </Group>
                <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                  <Dato label="Emisor" value={inv.emisorNombre} />
                  <Dato label="ID fiscal emisor" value={inv.emisorTaxId ?? "—"} />
                  <Dato label="Dirección emisor" value={inv.emisorDireccion ?? "—"} />
                  <Dato label="Receptor" value={inv.receptorNombre ?? "—"} />
                  <Dato label="RFC receptor" value={inv.receptorRfc} />
                  <Dato label="N° factura" value={inv.numeroFactura ?? "—"} />
                  <Dato label="Fecha" value={inv.fechaExpedicion ? inv.fechaExpedicion.slice(0, 10) : "—"} />
                  <Dato label="Total" value={`${inv.importeTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })} ${inv.moneda}`} />
                  <Dato label="Organización" value={sol.organizacion?.nombre ?? "—"} />
                </div>
                {inv.conceptos.length > 0 && (
                  <div className="overflow-x-auto rounded-md border border-border">
                    <Table verticalSpacing="xs" horizontalSpacing="sm">
                      <Thead>
                        <Tr>
                          <Th>Concepto</Th>
                          <Th className="text-right">Importe</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {inv.conceptos.map((c, i) => (
                          <Tr key={i}>
                            <Td>{c.descripcion}</Td>
                            <Td className="text-right font-mono">
                              {c.importe.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </div>
                )}
                <Text size="xs" c="dimmed">
                  Revisa que los datos sean correctos. El prorrateo se cuadrará contra el total extraído.
                </Text>
              </Stack>
            </Card>
          )}

          {inv && aplicaValidaciones && !cargar.isPending && <ResultadoValidacionesInvoice inv={inv} />}

          <Group justify="flex-end">
            <Button
              rightSection={<IconArrowRight size={16} />}
              disabled={!inv || (aplicaValidaciones && !invoiceValido(inv))}
              onClick={() => navigate({ to: "/solicitudes/$id/datos-generales", params: { id } })}
            >
              Continuar
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}

function Dato({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <Text size="xs" c="dimmed" tt="uppercase" className="tracking-wide">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {value}
      </Text>
    </div>
  );
}
